import React, { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { financeRollup, inTimeframe, TIMEFRAMES, money } from '../lib/data';
import { useWorklist, useLocations } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import Stat from '../components/Stat';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, TrendingUp, AlertCircle, CalendarClock, ShieldCheck, Bot, Download } from 'lucide-react';

const TYPE_TONE = { 'New patient': '#6366f1', 'Follow-up': '#0ea5e9', Procedure: '#8b5cf6', Surgery: '#f59e0b' };

function Bars({ rows, colorFor }) {
  const max = Math.max(...rows.map((r) => r.revenue), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.key}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-700 font-medium">{r.key}</span>
            <span className="text-slate-500 tnum">{r.count} appt{r.count === 1 ? '' : 's'} · {money(r.revenue)}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.revenue / max) * 100}%`, background: colorFor ? colorFor(r.key) : '#6366f1' }} /></div>
        </div>
      ))}
      {rows.length === 0 && <div className="text-xs text-slate-400">No appointments in scope.</div>}
    </div>
  );
}

export default function Finance() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const scopeAll = practiceId === 'all';

  const [timeframe, setTimeframe] = useState('all');
  const [location, setLocation] = useState('all');
  const [payer, setPayer] = useState('all');

  const { data: base = [] } = useWorklist(practiceId);
  const { data: locations = [] } = useLocations(practiceId);
  const payers = useMemo(() => Array.from(new Map(base.map((w) => [w.payer?.id, w.payer]).filter(([id]) => id)).values()), [base]);

  const items = base
    .filter((w) => inTimeframe(w.apt.startTime, timeframe))
    .filter((w) => location === 'all' || w.apt.locationId === location)
    .filter((w) => payer === 'all' || w.payer?.id === payer);
  const f = useMemo(() => financeRollup(items), [items]);
  const avg = f.count ? f.projected / f.count : 0;
  const selCls = 'h-9 w-auto min-w-[130px] bg-white text-sm';

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="CFO view" title="Finance & planning" subtitle="Practice-wide visibility for budget planning — slice by date, department, and payer.">
        <Button variant="outline"><Download className="h-4 w-4 mr-1.5" />Export</Button>
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="seg">{TIMEFRAMES.map((t) => <button key={t.k} className={timeframe === t.k ? 'active' : ''} onClick={() => setTimeframe(t.k)}>{t.label}</button>)}</div>
        <Select value={location} onValueChange={setLocation}><SelectTrigger className={selCls}><SelectValue placeholder="Department" /></SelectTrigger><SelectContent><SelectItem value="all">All departments</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
        <Select value={payer} onValueChange={setPayer}><SelectTrigger className={selCls}><SelectValue placeholder="Payer" /></SelectTrigger><SelectContent><SelectItem value="all">All payers</SelectItem>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Projected collections" value={money(f.projected)} sub={`${f.count} appointments`} icon={DollarSign} tone="emerald" />
        <Stat label="Patient resp. at stake" value={money(f.atStake)} sub="high-risk visits" icon={AlertCircle} tone="rose" />
        <Stat label="Avg per visit" value={money(avg)} sub="contracted-rate basis" icon={TrendingUp} tone="indigo" />
        <Stat label="Appointments" value={f.count} sub={scopeAll ? 'across all practices' : 'in scope'} icon={CalendarClock} tone="sky" />
      </div>

      <div className={`grid gap-4 ${scopeAll ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4">By visit type</div>
          <Bars rows={f.byType} colorFor={(k) => TYPE_TONE[k] || '#6366f1'} />
        </div>
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4">By insurance provider</div>
          <Bars rows={f.byPayer} />
        </div>
        {scopeAll && (
          <div className="dh-card p-5">
            <div className="text-sm font-semibold text-slate-900 mb-4">By practice</div>
            <Bars rows={f.byPractice} colorFor={() => '#8b5cf6'} />
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ai-surface p-4">
          <div className="flex items-center gap-2 mb-1.5 text-[11px] text-violet-700 font-medium"><Bot className="h-3.5 w-3.5" />Planning signal</div>
          <p className="text-sm text-slate-700">Many plans reset deductibles on Jan 1 — patient responsibility spikes early in the year. Enforce <b>card-on-file</b> and <b>collect-upfront</b> policies for high-deductible visits during reset periods to protect cash flow.</p>
        </div>
        <div className="dh-card p-4 flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-700"><b>HIPAA scope:</b> the platform processes patient data only when it's tied to a scheduled appointment. This finance view aggregates appointment economics — no PHI is needed to plan budgets.</div>
        </div>
      </div>
    </div>
  );
}
